/**
 * InvoiceService
 * Service for Invoice entity operations
 * Route: api/invoice
 * Controller: InvoiceController.cs
 */
import { CrudService } from '../base/CrudService';
/**
 * InvoiceService
 * Handles invoice CRUD operations and custom endpoints
 * Maps to /api/invoice endpoint
 */
export class InvoiceService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? InvoiceService.DEFAULT_SERVICE_PATH,
            basePath: InvoiceService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== CUSTOM METHODS ====================
    /**
     * Get invoice with enriched model view by ID
     * GET /api/invoice/view/{id}
     * @param id - Invoice ID
     * @returns Enriched invoice model view
     */
    async getViewById(id) {
        const response = await this.getViewByIdRaw(id);
        if (!response.data) {
            throw new Error(`Invoice view with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get invoice view by ID (raw)
     * @param id - Invoice ID
     * @returns ResponseData with enriched model view
     */
    getViewByIdRaw(id) {
        const endpoint = this.getEndpoint('view', `view/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get all invoice views (enriched with lookup data)
     * GET /api/invoice/view
     * @returns Array of enriched invoice model views
     */
    async getAllViews() {
        const response = await this.getAllViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get all invoice views (raw)
     * @returns ResponseData with array of enriched model views
     */
    getAllViewsRaw() {
        const endpoint = this.getEndpoint('allViews', 'view');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get invoice views by purchase request ID
     * GET /api/invoice/request/{requestId}/view
     * @param requestId - Purchase request ID
     * @returns Array of enriched invoice model views for the request
     */
    async getViewsByRequest(requestId) {
        const response = await this.getViewsByRequestRaw(requestId);
        return response.data ?? [];
    }
    /**
     * Get invoice views by request (raw)
     * @param requestId - Purchase request ID
     * @returns ResponseData with array of enriched model views
     */
    getViewsByRequestRaw(requestId) {
        const endpoint = this.getEndpoint('viewsByRequest', `request/${requestId}/view`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get filtered list of invoice views (enriched with lookup data)
     * POST /api/invoice/list-view
     * @param filter - Optional filter criteria
     * @returns Array of enriched invoice model views
     */
    async getListView(filter) {
        const response = await this.getListViewRaw(filter);
        return response.data ?? [];
    }
    /**
     * Get filtered list of invoice views (raw)
     * @param filter - Optional filter criteria
     * @returns ResponseData with array of enriched model views
     */
    getListViewRaw(filter) {
        const endpoint = this.getEndpoint('listView', 'list-view');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, filter || {});
    }
    /**
     * Create an invoice with line items in a single call.
     * POST /api/invoice/create-with-lines
     * Auto-advances request status to Invoiced (5) if first invoice.
     */
    async createWithLines(model) {
        const url = this.buildUrl('create-with-lines');
        const response = await this.http.post(url, model);
        if (!response.data)
            throw new Error('Failed to create invoice');
        return response.data;
    }
}
InvoiceService.DEFAULT_SERVICE_PATH = 'procurements';
InvoiceService.BASE_PATH = 'api/invoice';
//# sourceMappingURL=InvoiceService.js.map